import { Button, Col, PageBody, ScrollArea, Spinner, Stack, Text, ThemeProvider } from "@rally/ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Brand } from "./brand.tsx";
import { CallMediaProvider } from "./call/media-session.tsx";
import { collapseFileFocus, enterFileFocus, focusFrom, focusIsHere } from "./chat/attachment/file-focus.ts";
import { forgetEveryDraft } from "./chat/composer/draft.ts";
import { DraftOwnerProvider } from "./chat/composer/draft-owner.tsx";
import { forgetEverything } from "./chat/kept.ts";
import { conversationToRestore } from "./chat/last-visited.ts";
import { Record } from "./chat/message/record.tsx";
import { Beside } from "./chat/panel/beside.tsx";
import { Column, PERSONAL } from "./chat/panel/column.tsx";
import { Panels, type Stacking, StackProvider, StandalonePanel } from "./chat/panel/panels.tsx";
import { showDetails } from "./chat/panel/showing.ts";
import {
  drawnFor,
  nothingOpen,
  back as onePanelBack,
  onlyOne,
  opened as openPanel,
  type Stack as PanelStack,
} from "./chat/panel/stacking.ts";
import { Feed } from "./feed/feed.tsx";
import { Frame } from "./frame.tsx";
import { LocaleProvider, useLocale } from "./localization/provider.tsx";
import { say } from "./localization/say.ts";
import { Activity } from "./me/activity.tsx";
import { Agents } from "./me/agents.tsx";
import { Calendar } from "./me/calendar.tsx";
import { isAccountBody } from "./me/destinations.ts";
import { Integrations } from "./me/integrations.tsx";
import { Machines } from "./me/machines.tsx";
import { act, leave } from "./net/rally.ts";
import { Looking } from "./push/looking.ts";
import { goTo, replaceWith, useWhere } from "./router.ts";
import { AppStanding } from "./screens/app-standing.tsx";
import { PersonalScreen } from "./screens/personal.tsx";
import { ProjectScreen } from "./screens/project.tsx";
import { Register } from "./screens/register.tsx";
import { SignIn } from "./screens/sign-in.tsx";
import { Find } from "./search/find.tsx";
import { searchResourceCard } from "./search/resource.tsx";
import { useSession } from "./session.ts";
import { Settings } from "./settings.tsx";
import { Ears } from "./sound/ears.tsx";
import { useStepping } from "./step.ts";
import { Who } from "./who.tsx";

function Screens({ session, again }: ReturnType<typeof useSession>) {
  useLocale();
  const where = useWhere();
  const arrived = () => {
    again();
    goTo("/");
  };

  const atTheDoor =
    where.screen === "register" && where.invite ? (
      <Register invite={where.invite} onIn={arrived} />
    ) : session.state === "signed-out" ? (
      <SignIn onIn={arrived} />
    ) : session.state === "broke" ? (
      <Refusal why={session.why} again={again} />
    ) : session.state === "asking" ? (
      <Waiting />
    ) : null;

  const me = session.state === "signed-in" ? session.me : null;

  const restoreOnArrival = useRef(where.screen === "home" || where.screen === "sign-in");
  const restoreTried = useRef(new Set<string>());
  useEffect(() => {
    const account = me?.account;
    if (
      !account ||
      !restoreOnArrival.current ||
      where.screen !== "home" ||
      restoreTried.current.has(account)
    ) {
      return;
    }
    restoreTried.current.add(account);
    void conversationToRestore(account).then((remembered) => {
      if (remembered && window.location.pathname === "/") replaceWith(`/c/${remembered}`);
    });
  }, [me?.account, where.screen]);

  const conversation = where.screen === "conversation" ? (where.conversation ?? "") : "";
  const panelOwner = where.screen === "project" ? `project:${where.project ?? ""}` : conversation;
  const [panels, setPanels] = useState<PanelStack>(() => nothingOpen(panelOwner));
  const currentPanel = panels.cards[0]?.key ?? null;
  const addressedFocus = focusFrom(where.path);
  const focused = focusIsHere(addressedFocus, conversation, currentPanel);
  useEffect(() => {
    if (!where.focus || window.history.state?.rallyFileFocus) return;
    window.history.replaceState(null, "", collapseFileFocus(where.path));
    window.history.pushState({ rallyFileFocus: true }, "", where.path);
  }, [where.focus, where.path]);
  const collapseFocus = useCallback(() => {
    if (window.history.state?.rallyFileFocus) window.history.back();
    else replaceWith(collapseFileFocus(where.path));
  }, [where.path]);
  const stacking = useMemo<Stacking>(
    () => ({
      current: currentPanel,
      me: me?.account ?? null,
      focused,
      open: (card) => setPanels((held) => openPanel(held, panelOwner, card)),
      focus: () => {
        const file = currentPanel?.startsWith("file:") ? currentPanel.slice(5) : null;
        if (!file || focused) return;
        const path = enterFileFocus(where.path, conversation, file);
        window.history.pushState({ rallyFileFocus: true }, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
      },
      collapseFocus,
      leaveFocus: (path) => {
        setPanels(nothingOpen(panelOwner));
        replaceWith(path);
      },
      back: () => setPanels(onePanelBack),
      close: () => {
        setPanels(nothingOpen(panelOwner));
        showDetails(false);
        if (where.screen === "resource") goTo("/");
      },
    }),
    [collapseFocus, conversation, currentPanel, focused, me?.account, panelOwner, where.path, where.screen],
  );
  useEffect(() => setPanels(nothingOpen(panelOwner)), [panelOwner]);

  useStepping(where.conversation ?? null, me !== null && atTheDoor === null);

  const inside =
    me && !atTheDoor ? (
      where.screen === "conversation" && where.conversation ? (
        <Record
          conversation={where.conversation}
          me={me}
          message={where.message ?? null}
          file={where.file ?? null}
          app={where.app ?? null}
        />
      ) : where.screen === "app" && where.app ? (
        <AppStanding app={where.app} />
      ) : where.screen === "project" && where.project === PERSONAL ? (
        <PersonalScreen />
      ) : where.screen === "project" && where.project ? (
        <ProjectScreen project={where.project} />
      ) : where.screen === "me" && isAccountBody(where.section) ? (
        <PageBody testID="me-screen">
          {where.section === "activity" ? (
            <Activity me={me.account} />
          ) : where.section === "agents" ? (
            <Agents me={me.account} runsIt={me.role === "owner" || me.role === "admin"} />
          ) : where.section === "machines" ? (
            <Machines />
          ) : where.section === "integrations" ||
            where.section === "providers" ||
            where.section === "integrations" ? (
            <Integrations me={me.account} />
          ) : (
            <Calendar />
          )}
        </PageBody>
      ) : where.screen === "me" ? (
        <Settings
          visible={where.screen === "me" && !isAccountBody(where.section)}
          at={where.section ?? "profile"}
          me={me.account}
          runsIt={me.role === "owner" || me.role === "admin"}
          onGo={(section) => goTo(`/me/${section}`)}
          onClose={() => goTo("/")}
          onChanged={again}
          onLeave={() => {
            forgetEverything();

            forgetEveryDraft();
            void leave().finally(arrived);
          }}
        />
      ) : (
        <Empty />
      )
    ) : null;

  return (
    <StackProvider value={stacking}>
      <DraftOwnerProvider account={me?.account ?? null}>
        <CallMediaProvider me={me?.account ?? null}>
          <Frame
            even={atTheDoor !== null}
            focused={focused}
            at={where.path}
            brand={(size) => <Brand big={atTheDoor !== null} within={size} />}
            list={
              me && !atTheDoor ? (
                <Column at={where.conversation ?? null} projectAt={where.project ?? null} />
              ) : null
            }
            account={me && !atTheDoor ? <Who me={me} /> : null}
            search={me && !atTheDoor ? <Find /> : null}
            main={atTheDoor ?? inside}
            details={
              me && !atTheDoor && where.screen === "conversation" && where.conversation ? (
                <Beside conversation={where.conversation} me={me.account} stack={panels} />
              ) : me && !atTheDoor && where.screen === "project" ? (
                <Panels standing={null} open={onlyOne(drawnFor(panels, panelOwner))} />
              ) : me && !atTheDoor && where.screen === "resource" && where.resource ? (
                <StandalonePanel card={searchResourceCard(where.resource)} />
              ) : me &&
                !atTheDoor &&
                where.screen === "me" &&
                isAccountBody(where.section) &&
                panels.cards[0] ? (
                <StandalonePanel card={panels.cards[0]} />
              ) : null
            }
          />
          {me ? <Looking /> : null}
          {me ? <Ears me={{ account: me.account, handle: me.handle }} /> : null}
        </CallMediaProvider>
      </DraftOwnerProvider>
    </StackProvider>
  );
}

function Empty() {
  return (
    <ScrollArea
      contentContainerStyle={{
        padding: 16,
        gap: 8,
        width: "100%",
      }}
    >
      <Feed
        scope="server"
        title={say("home.title")}
        limit={40}
        emptyAction={
          <Button
            icon="plus"
            onPress={() => {
              void act<{ id: string }>("conversation.create", {}).then((made) => goTo(`/c/${made.id}`));
            }}
            testID="home-new-conversation"
          >
            {say("chat.new")}
          </Button>
        }
      />
    </ScrollArea>
  );
}

const RETRY_MS = 5000;

function Refusal({ why, again }: { why: string; again: () => void }) {
  useEffect(() => {
    const timer = setInterval(again, RETRY_MS);
    return () => clearInterval(timer);
  }, [again]);
  return (
    <Col style={{ flex: 1, alignItems: "center", justifyContent: "center" }} testID="refusal">
      <Stack gap={4} align="center" style={{ maxWidth: 360, paddingHorizontal: 24 }}>
        <Text typeName="body" color="text.secondary" style={{ textAlign: "center" }}>
          {why}
        </Text>
        <Text typeName="caption" color="text.tertiary" style={{ textAlign: "center" }}>
          {say("app.retrying")}
        </Text>
        <Button variant="outline" onPress={again} testID="refusal-again">
          {say("app.tryAgain")}
        </Button>
      </Stack>
    </Col>
  );
}

function Waiting() {
  return (
    <Col style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
      <Stack gap={3} align="center">
        <Spinner label={say("app.opening")} />
      </Stack>
    </Col>
  );
}

export function App() {
  const sessionState = useSession();
  const account = sessionState.session.state === "signed-in" ? sessionState.session.me.account : undefined;
  return (
    <LocaleProvider account={account}>
      <ThemeProvider>
        <Screens {...sessionState} />
      </ThemeProvider>
    </LocaleProvider>
  );
}
